import React from 'react';

import { Form, Input, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { ResizeCallbackData } from 'react-resizable';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { NSKeys } from '@app/i18n';
import { RetrieveWarehouseSkuSchema } from '@app/models/wms';
import { WAREHOUSE_IMPORT_ERRORS_TABLE_COLUMNS } from '@app/services/appSettings';
import { useTableParams } from '@app/services/hooks/useTableParams';
import { TableCellEllipsisText } from '@components/TableCellEllipsisText';
import { TableDnDResizable } from '@components/TableDnDResizable';
import { TABS } from '@pages/Warehouses/pages/Single/interfaces';

import { useGetWarehouseSkusQuery } from '../../../../hooks';

export const ImportErrors: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const warehousesT = useTranslation(NSKeys.warehouses);
  const commonT = useTranslation(NSKeys.common);
  const validationT = useTranslation(NSKeys.validation);
  const { current, pageSize, setPagination } = useTableParams();
  const effectiveSearchValue = searchParams.get('q') || searchParams.get('code') || '';
  const [searchInputValue, setSearchInputValue] = React.useState(effectiveSearchValue);

  const { data, isLoading } = useGetWarehouseSkusQuery({
    warehouse_uid: params.id || '',
    offset: (current - 1) * pageSize,
    limit: pageSize,
    has_import_error: true,
    q: effectiveSearchValue || '',
  });

  React.useEffect(() => {
    setSearchInputValue(effectiveSearchValue);
  }, [effectiveSearchValue]);

  const handleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams);

    if (value) {
      params.set('q', value);
    } else {
      params.delete('q');
    }

    params.delete('code');
    params.set('current', '1');
    setSearchParams(params);
  };

  const columns = React.useMemo<ColumnsType<RetrieveWarehouseSkuSchema>>(
    () => [
      {
        title: warehousesT.t('tableColumns.sku'),
        dataIndex: 'ref_id',
        key: 'sku',
        width: 360,
        ellipsis: true,
        render: (_: string, record) => {
          const query = record.code || record.ref_id || record.title || '';

          return (
            <Typography.Link
              style={{ display: 'block', width: '100%' }}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/dashboard/warehouses/${params.id}/${TABS.NOMENCLATURE}?q=${encodeURIComponent(query)}`);
              }}>
              <Typography.Text strong style={{ display: 'block' }}>
                {record.code || '—'}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                ellipsis={{ tooltip: record.ref_id || '—' }}
                style={{ display: 'block' }}>
                {record.ref_id || '—'}
              </Typography.Text>
              <Typography.Text ellipsis={{ tooltip: record.title || '—' }} style={{ display: 'block', width: '100%' }}>
                {record.title || '—'}
              </Typography.Text>
            </Typography.Link>
          );
        },
      },
      {
        title: warehousesT.t('tableColumns.import_error_description'),
        dataIndex: 'import_error_code',
        key: 'import_error_description',
        width: 520,
        ellipsis: true,
        render: () => (
          <TableCellEllipsisText value={validationT.t('warehouse_import_duplicate_code_description') as string} />
        ),
      },
    ],
    [navigate, params.id, validationT, warehousesT]
  );

  const storageKey = `${params.id || 'warehouse'}${WAREHOUSE_IMPORT_ERRORS_TABLE_COLUMNS}`;
  const [localStorageColumns, setLocalStorageColumns] = useLocalStorage(
    storageKey,
    columns.map(({ render, ...rest }) => rest)
  );
  const totalTableWidth = React.useMemo(
    () => localStorageColumns.reduce((sum, column) => sum + Number(column.width || 0), 0),
    [localStorageColumns]
  );

  const handleResize =
    (index: number) =>
    (_: React.SyntheticEvent<Element>, { size }: ResizeCallbackData) => {
      const newColumns = [...localStorageColumns];
      newColumns[index] = {
        ...newColumns[index],
        width: size.width > 20 ? size.width : 20,
      };
      setLocalStorageColumns(newColumns);
    };

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}>
        <Form.Item style={{ marginBottom: 0 }}>
          <Input.Search
            style={{ width: 280 }}
            size="middle"
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            placeholder={commonT.t('search')}
            enterButton
            disabled={isLoading}
            onSearch={handleSearch}
          />
        </Form.Item>
      </div>
      <TableDnDResizable<RetrieveWarehouseSkuSchema>
        localStorageName={storageKey}
        dataSource={data?.items}
        rowKey="uid"
        className="tableClassname"
        columns={localStorageColumns.map((column, index) => ({
          ...column,
          onHeaderCell: (column: any) => ({
            width: column.width,
            onResize: handleResize(index) as React.ReactEventHandler<any>,
          }),
          render: columns.find((baseColumn) => baseColumn.key === column.key)?.render,
        }))}
        onColumnsChange={setLocalStorageColumns}
        loading={isLoading}
        tableLayout="fixed"
        scroll={{ x: totalTableWidth }}
        pagination={{
          current,
          pageSize,
          total: data?.total,
          showQuickJumper: true,
          showSizeChanger: true,
          position: ['bottomCenter'],
          onChange: (newPage, newSize) => {
            setPagination(newPage, newSize);
          },
          showTotal: (total, range) => `${range[0]}-${range[1]} ${commonT.t('paginationOf')} ${total}`,
        }}
      />
    </>
  );
};
